import { useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../environment";
import { cachedRequest } from "../utils/requestCache";

export interface UseFilterOptionsFlags {
  teams?: boolean;
  users?: boolean;
  companies?: boolean;
  industryTypes?: boolean;
  businessTypes?: boolean;
  leads?: boolean;
  pipelines?: boolean;
  deals?: boolean;
  proposals?: boolean;
  lifecycleSources?: boolean;
}

export const useFilterOptions = (
  selectedTenantId: string | number | null,
  needed?: UseFilterOptionsFlags,
  enabled: boolean = true
) => {
  const needTeams = !!needed?.teams;
  const needUsers = !!needed?.users;
  const needCompanies = !!needed?.companies;
  const needIndustryTypes = !!needed?.industryTypes;
  const needBusinessTypes = !!needed?.businessTypes;
  const needLeads = !!needed?.leads;
  const needPipelines = !!needed?.pipelines;
  const needDeals = !!needed?.deals;
  const needProposals = !!needed?.proposals;
  const needLifecycleSources = !!needed?.lifecycleSources;

  const [teams, setTeams] = useState<{ value: number; label: string }[]>([]);
  const [users, setUsers] = useState<{ value: number; label: string }[]>([]);
  const [companies, setCompanies] = useState<{ value: number; label: string }[]>([]);
  const [industryTypes, setIndustryTypes] = useState<{ value: number; label: string }[]>([]);
  const [businessTypes, setBusinessTypes] = useState<{ value: number; label: string }[]>([]);

  // New relational options
  const [leads, setLeads] = useState<{ value: number; label: string }[]>([]);
  const [pipelines, setPipelines] = useState<{ value: number; label: string }[]>([]);
  const [deals, setDeals] = useState<{ value: number; label: string }[]>([]);
  const [proposals, setProposals] = useState<{ value: number; label: string }[]>([]);
  const [lifecycleSources, setLifecycleSources] = useState<{ value: number; label: string }[]>([]);

  const [isLoading, setIsLoading] = useState({
    teams: false,
    users: false,
    companies: false,
    industryTypes: false,
    businessTypes: false,
    leads: false,
    pipelines: false,
    deals: false,
    proposals: false,
    lifecycleSources: false,
  });

  const getAuthHeaders = () => {
    const authHeader = localStorage.getItem("authCred");
    return {
      "Content-Type": "application/json",
      Authorization: `Basic ${authHeader}`,
    };
  };

  const fetchOptions = useCallback(async () => {
    if (!selectedTenantId || !enabled) return;

    const fetchTeams = async () => {
      setIsLoading(prev => ({ ...prev, teams: true }));
      try {
        const results = await cachedRequest(`filter-teams:${selectedTenantId}`, async () => {
          const res = await fetch(`${API_BASE_URL}/team/?tenant_id=${selectedTenantId}&page=1&page_size=100&response_mode=light`, { headers: getAuthHeaders() });
          const data = await res.json();
          return Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : [];
        });
        setTeams(results.map((t: any) => ({ value: t.team_id, label: t.team_name || t.name })));
      } catch (err) { }
      finally { setIsLoading(prev => ({ ...prev, teams: false })); }
    };

    const fetchUsers = async () => {
      setIsLoading(prev => ({ ...prev, users: true }));
      try {
        const results = await cachedRequest(`filter-tenant-users:${selectedTenantId}`, async () => {
          const res = await fetch(`${API_BASE_URL}/tenant-user/?tenant_id=${selectedTenantId}&page=1&page_size=200&response_mode=light`, { headers: getAuthHeaders() });
          const data = await res.json();
          return Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : [];
        });
        setUsers(results.map((u: any) => ({ value: u.user_id, label: `${u.user_first_name} ${u.user_last_name}` })));
      } catch (err) { }
      finally { setIsLoading(prev => ({ ...prev, users: false })); }
    };

    const fetchCompanies = async () => {
      setIsLoading(prev => ({ ...prev, companies: true }));
      try {
        const results = await cachedRequest(`filter-companies:${selectedTenantId}`, async () => {
          const res = await fetch(`${API_BASE_URL}/company/?tenant_id=${selectedTenantId}&page=1&page_size=200&response_mode=light`, { headers: getAuthHeaders() });
          const data = await res.json();
          return Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : [];
        });
        setCompanies(results.map((c: any) => ({ value: c.company_id, label: c.company_name })));
      } catch (err) { }
      finally { setIsLoading(prev => ({ ...prev, companies: false })); }
    };

    const fetchIndustryTypes = async () => {
      setIsLoading(prev => ({ ...prev, industryTypes: true }));
      try {
        const results = await cachedRequest(`filter-industry-types`, async () => {
          const res = await fetch(`${API_BASE_URL}/industry-type/?page=1&page_size=200&response_mode=light`, { headers: getAuthHeaders() });
          const data = await res.json();
          return Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : [];
        });
        setIndustryTypes(results.map((i: any) => ({ value: i.industry_type_id, label: i.industry_type_display_name })));
      } catch (err) { }
      finally { setIsLoading(prev => ({ ...prev, industryTypes: false })); }
    };

    const fetchBusinessTypes = async () => {
      setIsLoading(prev => ({ ...prev, businessTypes: true }));
      try {
        const results = await cachedRequest(`filter-business-types`, async () => {
          const res = await fetch(`${API_BASE_URL}/business-type/?page=1&page_size=200&response_mode=light`, { headers: getAuthHeaders() });
          const data = await res.json();
          return Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : [];
        });
        setBusinessTypes(results.map((b: any) => ({ value: b.business_type_id, label: b.business_type_display_name })));
      } catch (err) { }
      finally { setIsLoading(prev => ({ ...prev, businessTypes: false })); }
    };

    const fetchLeads = async () => {
      setIsLoading(prev => ({ ...prev, leads: true }));
      try {
        const results = await cachedRequest(`filter-leads:${selectedTenantId}`, async () => {
          const res = await fetch(`${API_BASE_URL}/lead/?tenant_id=${selectedTenantId}&page=1&page_size=200&response_mode=light`, { headers: getAuthHeaders() });
          const data = await res.json();
          return Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : [];
        });
        setLeads(results.map((l: any) => ({ value: l.lead_id, label: l.title || l.lead_name })));
      } catch (err) { }
      finally { setIsLoading(prev => ({ ...prev, leads: false })); }
    };

    const fetchPipelines = async () => {
      setIsLoading(prev => ({ ...prev, pipelines: true }));
      try {
        const results = await cachedRequest(`filter-pipelines:${selectedTenantId}`, async () => {
          const res = await fetch(`${API_BASE_URL}/pipeline/?tenant_id=${selectedTenantId}&page=1&page_size=200&response_mode=light`, { headers: getAuthHeaders() });
          const data = await res.json();
          return Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : [];
        });
        setPipelines(results.map((p: any) => ({ value: p.pipeline_id, label: p.title || p.pipeline_name })));
      } catch (err) { }
      finally { setIsLoading(prev => ({ ...prev, pipelines: false })); }
    };

    const fetchDeals = async () => {
      setIsLoading(prev => ({ ...prev, deals: true }));
      try {
        const results = await cachedRequest(`filter-deals:${selectedTenantId}`, async () => {
          const res = await fetch(`${API_BASE_URL}/deal/?tenant_id=${selectedTenantId}&page=1&page_size=200&response_mode=light`, { headers: getAuthHeaders() });
          const data = await res.json();
          return Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : [];
        });
        setDeals(results.map((d: any) => ({ value: d.deal_id, label: d.title || d.deal_name })));
      } catch (err) { }
      finally { setIsLoading(prev => ({ ...prev, deals: false })); }
    };

    const fetchProposals = async () => {
      setIsLoading(prev => ({ ...prev, proposals: true }));
      try {
        const results = await cachedRequest(`filter-proposals:${selectedTenantId}`, async () => {
          const res = await fetch(`${API_BASE_URL}/proposal/?tenant_id=${selectedTenantId}&page=1&page_size=200&response_mode=light`, { headers: getAuthHeaders() });
          const data = await res.json();
          return Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : [];
        });
        setProposals(results.map((p: any) => ({ value: p.proposal_id, label: p.title || p.subject })));
      } catch (err) { }
      finally { setIsLoading(prev => ({ ...prev, proposals: false })); }
    };

    const fetchLifecycleSources = async () => {
      setIsLoading(prev => ({ ...prev, lifecycleSources: true }));
      try {
        const results = await cachedRequest(`filter-lifecycle-sources:${selectedTenantId}`, async () => {
          const res = await fetch(`${API_BASE_URL}/lifecycle-source/?tenant_id=${selectedTenantId}&page=1&page_size=100&response_mode=light`, { headers: getAuthHeaders() });
          const data = await res.json();
          return Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : [];
        });
        setLifecycleSources(results.map((s: any) => ({ value: s.lifecycle_source_id, label: s.lifecycle_source_display_name })));
      } catch (err) { }
      finally { setIsLoading(prev => ({ ...prev, lifecycleSources: false })); }
    };

    Promise.all([
      needTeams ? fetchTeams() : Promise.resolve(),
      needUsers ? fetchUsers() : Promise.resolve(),
      needCompanies ? fetchCompanies() : Promise.resolve(),
      needIndustryTypes ? fetchIndustryTypes() : Promise.resolve(),
      needBusinessTypes ? fetchBusinessTypes() : Promise.resolve(),
      needLeads ? fetchLeads() : Promise.resolve(),
      needPipelines ? fetchPipelines() : Promise.resolve(),
      needDeals ? fetchDeals() : Promise.resolve(),
      needProposals ? fetchProposals() : Promise.resolve(),
      needLifecycleSources ? fetchLifecycleSources() : Promise.resolve(),
    ]);
  }, [
    selectedTenantId,
    enabled,
    needTeams,
    needUsers,
    needCompanies,
    needIndustryTypes,
    needBusinessTypes,
    needLeads,
    needPipelines,
    needDeals,
    needProposals,
    needLifecycleSources,
  ]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  return {
    teams,
    users,
    companies,
    industryTypes,
    businessTypes,
    leads,
    pipelines,
    deals,
    proposals,
    lifecycleSources,
    isLoading
  };
};
